'use client'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Badge, Divider, Flex, TextInput, Title, Text } from '@mantine/core'
import { useContext, useEffect, useState } from 'react'
import { CheckIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useMantineTheme } from '@mantine/core'
import React from 'react'

import { StyledButton } from '~/app/_components/buttons'
import { ProfessionsFilter } from '~/app/_components/search/desktop/ProfessionsFilter'

import { AccountsFilterContext } from '../AccountsFilterContext'
import { mobileFilterAction, mobileFilterWrapper } from './styles.css'
import { CommonAutocompleteInput } from '~/app/(me)/_components'
import { inputWrapperBorder } from '~/app/iso/styles.css'
import { AccountType } from '~/generated/graphql'
import { appTrace } from '~/utils'
type SearchFilterProps = {
  closeFilters: () => void
}

export function SearchFilterMobile({ closeFilters }: SearchFilterProps) {
  const [height, setHeight] = useState(window.innerHeight)
  const { freeTextSearch, setFreeTextSearch, resetSearch, setAccountTypeSearch, setLocationSearch, locationSearch } =
    useContext(AccountsFilterContext)

  const [textSearchInput, setTextSearchInput] = useState<string>(freeTextSearch)
  const [locationSearchBadges, setLocationSearchBadges] = useState<string[]>([])

  function handleTextInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setTextSearchInput(event.target.value)
  }
  function handleFilter() {
    setFreeTextSearch(textSearchInput)
    closeFilters()
  }

  function handleReset() {
    setTextSearchInput('')
    resetSearch()
    closeFilters()
    setLocationSearch([])
    setAccountTypeSearch([])
  }

  useEffect(() => {
    if (!locationSearch.length) return
    setLocationSearchBadges(locationSearch)
  }, [locationSearch])

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  function handleLocationRemoval(location: string) {
    setLocationSearch((prevLocationSearch) => {
      return prevLocationSearch.filter((loc: string) => loc !== location)
    })
    setLocationSearchBadges((prevLocationSearchBadges) => {
      return prevLocationSearchBadges.filter((loc: string) => loc !== location)
    })
  }

  return (
    <>
      <Flex
        direction="column"
        justify="space-between"
        className={mobileFilterWrapper}
        data-testid="filter-modal"
        style={{
          height: height,
        }}
      >
        <Flex direction="column" style={{ overflowY: 'scroll' }} mt="50px" mx="lg">
          <Flex w="100%" justify="center" direction="column">
            <Title order={4} ta="left" w="100%" mb="6px">
              BY LOCATION
            </Title>
            <CommonAutocompleteInput
              autoclear={true}
              classNames={{
                input: inputWrapperBorder,
              }}
              onPlaceSelected={(place) => {
                setLocationSearch((prevLocationSearch) => {
                  const newLocation = place?.formatted_address ?? ''
                  return [...prevLocationSearch, newLocation]
                })

                setLocationSearchBadges((prevState) => [...prevState, place?.formatted_address])
              }}
            />
          </Flex>
          {locationSearchBadges.length ? (
            <Flex direction="row" gap="sm" px="md" pt="xs" pb={0} wrap="wrap">
              {locationSearchBadges.map((loc, i) => (
                <Badge
                  h={25}
                  c="black"
                  key={i}
                  leftSection={
                    <XMarkIcon height={15} onClick={(_e) => handleLocationRemoval(loc)} style={{ cursor: 'pointer' }} />
                  }
                >
                  {locationSearchBadges[i]}
                </Badge>
              ))}
            </Flex>
          ) : null}

          <Flex direction="column" style={{ borderTop: '1px solid black', paddingTop: '16px', marginTop: '30px' }}>
            <Title order={4} ta="center" w="100%" mb="50px">
              INDIVIDUAL CREATIVES
            </Title>
            <Title order={4} ta="left" w="100%" mb="6px">
              BY PROFESSION
            </Title>
            <ProfessionsFilter />
          </Flex>

          <Flex
            direction="column"
            style={{ borderTop: '1px solid black', paddingTop: '16px', marginTop: '30px', marginBottom: '50px' }}
          >
            <Title order={4} ta="center" w="100%" mb="50px">
              BUSINESS ACCOUNTS
            </Title>
            <Text size={'13px'} fw={600} ta="left" w="100%" mb="6px">
              BY BUSINESS TYPE
            </Text>
            <BusinessTypeFilter />
          </Flex>

          <Flex direction="column" style={{ borderTop: '1px solid black', paddingTop: '30px', marginBottom: '120px' }}>
            <Title order={4} ta="left" w="100%" mb="6px">
              BY KEYWORD
            </Title>
            <TextInput
              size="md"
              p="0px"
              placeholder="Search for name, brand or location"
              value={textSearchInput ?? freeTextSearch}
              onChange={handleTextInputChange}
              data-testid="mobile-search-input"
              classNames={{
                input: inputWrapperBorder,
              }}
            />
          </Flex>
          {/* <SearchFilterMobileSort /> */}
        </Flex>
        <Flex direction="column" w="100%" style={{ position: 'fixed', bottom: '0px' }}>
          <Divider />
          <Flex
            className={mobileFilterAction}
            w="100%"
            p="md"
            mb="md"
            justify="space-between"
            gap="sm"
            // style={{ position: 'fixed', bottom: '0px' }}
          >
            <StyledButton
              type="passive"
              text="RESET"
              fz="15px"
              size="xLarge"
              disablePadding={true}
              onClick={handleReset}
            />
            <StyledButton
              type="primary"
              text="FILTER"
              fz="15px"
              size="xLarge"
              onClick={handleFilter}
              testId="filter-button"
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

function BusinessTypeFilter() {
  const theme = useMantineTheme()
  const { accountTypeSearch, setAccountTypeSearch } = useContext(AccountsFilterContext)

  const businessTypes: { id: AccountType; name: string }[] = [
    { id: AccountType.Brand, name: 'Brand' },
    { id: AccountType.TalentAgency, name: 'Talent Agency' },
    { id: AccountType.School, name: 'School' },
    { id: AccountType.Publication, name: 'Publication' },
  ]

  const handleTypeClick = (type: AccountType) => {
    appTrace('handleTypeClick', { type })
    const toAdd = !accountTypeSearch.includes(type)
    const newTypes = toAdd ? [...accountTypeSearch, type] : accountTypeSearch.filter((accType) => accType !== type)
    setAccountTypeSearch(newTypes)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        justifyContent: 'space-between',
      }}
    >
      {businessTypes.map((type, i) => {
        const isSelected = accountTypeSearch.includes(type.id)

        return (
          <div
            key={i}
            role="button"
            tabIndex={0}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid black',
              width: '48%',
              marginBottom: '4px',
              padding: '5px',
              paddingLeft: '10px',
              fontWeight: '600',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: isSelected ? theme.colors.brand[4] : 'white',
            }}
            onClick={() => handleTypeClick(type.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleTypeClick(type.id)
              }
            }}
          >
            <Text size="md" fw={600}>
              {type.name}
            </Text>
            {isSelected ? <CheckIcon height={20} fontWeight={600} /> : <PlusIcon height={20} fontWeight={600} />}
          </div>
        )
      })}
    </div>
  )
}
